import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  Users,
  Shield,
  Calendar,
  Info,
  Trash2,
  Plus,
  Save,
  LogOut,
  UserPlus,
  X,
  Edit,
  Check,
  Download,
  Image,
  Play,
  Video,
  Upload,
  Link
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8f61f4ec-e0f3-4bd9-bbd1-92126e3d51b2/artifacts/4yf0ok29_Logo_Lactal.png";

const TEAM_COLORS = [
  { value: "#FFD700", label: "Dorado" },
  { value: "#DC2626", label: "Rojo" },
  { value: "#1E40AF", label: "Azul" },
  { value: "#87CEEB", label: "Celeste" },
  { value: "#059669", label: "Verde" },
  { value: "#000000", label: "Negro" },
  { value: "#FFFFFF", label: "Blanco" },
  { value: "#7C3AED", label: "Violeta" },
  { value: "#EA580C", label: "Naranja" },
  { value: "#EC4899", label: "Rosa" },
  { value: "#14B8A6", label: "Turquesa" },
  { value: "#6B7280", label: "Gris" },
  { value: "#8B4513", label: "Marrón" },
];

// Position badge component
const PositionBadge = ({ position }) => {
  const badgeClass = {
    "Golero": "bg-blue-600",
    "Defensa": "bg-green-600",
    "Volante": "bg-yellow-600",
    "Delantero": "bg-red-600"
  }[position] || "bg-gray-600";

  return (
    <span className={`${badgeClass} px-2 py-0.5 text-xs font-semibold rounded uppercase`}>
      {position}
    </span>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authLoading, setAuthLoading] = useState(false);
  
  const [participantes, setParticipantes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [partidos, setPartidos] = useState([]);
  const [galeria, setGaleria] = useState([]);
  const [infoTorneo, setInfoTorneo] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showNewMatchModal, setShowNewMatchModal] = useState(false);
  const [showAssignPlayerModal, setShowAssignPlayerModal] = useState(false);
  const [showEditPlayerModal, setShowEditPlayerModal] = useState(false);
  const [showNewGaleriaModal, setShowNewGaleriaModal] = useState(false);
  const [showEditGaleriaModal, setShowEditGaleriaModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editingResult, setEditingResult] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingGaleriaItem, setEditingGaleriaItem] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingPartido, setEditingPartido] = useState(null);
  const [showEditPartidoModal, setShowEditPartidoModal] = useState(false);

  const [newTeam, setNewTeam] = useState({ nombre: "", color: "#FFD700" });
  const [newMatch, setNewMatch] = useState({
    equipo_local_id: "",
    equipo_visitante_id: "",
    fecha: "",
    hora: "",
    cancha: "",
    fase: "Fecha 1"
  });
  const [newGaleriaItem, setNewGaleriaItem] = useState({
    tipo: "imagen",
    url: "",
    titulo: "",
    descripcion: "",
    thumbnail: ""
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"

  const FASES_TORNEO = [
    "Fecha 1",
    "Fecha 2", 
    "Fecha 3",
    "Semifinales",
    "Tercer Puesto",
    "Final"
  ];

  const getAuthHeaders = useCallback(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials]);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [partRes, equiposRes, partidosRes, infoRes, galeriaRes] = await Promise.all([
        axios.get(`${API}/admin/participantes`, { headers }),
        axios.get(`${API}/equipos/con-jugadores`),
        axios.get(`${API}/partidos/con-equipos`),
        axios.get(`${API}/info-torneo`),
        axios.get(`${API}/admin/galeria`, { headers })
      ]);
      setParticipantes(partRes.data);
      setEquipos(equiposRes.data);
      setPartidos(partidosRes.data);
      setInfoTorneo(infoRes.data);
      setGaleria(galeriaRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error("Sesión expirada");
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      await axios.post(`${API}/admin/login`, {}, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setIsAuthenticated(true);
      toast.success("Bienvenido, admin!");
    } catch (error) {
      toast.error("Credenciales incorrectas");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: "", password: "" });
    navigate("/");
  };

  const handleDeleteParticipante = async (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este participante?")) return;
    try {
      await axios.delete(`${API}/admin/participantes/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Participante eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer({ ...player });
    setShowEditPlayerModal(true);
  };

  const handleUpdatePlayer = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/participantes/${editingPlayer.id}`, {
        nombre: editingPlayer.nombre,
        apellido: editingPlayer.apellido,
        apodo: editingPlayer.apodo,
        edad: parseInt(editingPlayer.edad),
        telefono: editingPlayer.telefono || "",
        posicion: editingPlayer.posicion,
        nivel: parseInt(editingPlayer.nivel),
        comida_final: editingPlayer.comida_final
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Jugador actualizado");
      setShowEditPlayerModal(false);
      setEditingPlayer(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/equipos`, newTeam, {
        headers: getAuthHeaders()
      });
      toast.success("Equipo creado");
      setShowNewTeamModal(false);
      setNewTeam({ nombre: "", color: "#FFD700" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear equipo");
    }
  };

  const handleDeleteTeam = async (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este equipo? Se quitarán los jugadores asignados.")) return;
    try {
      await axios.delete(`${API}/admin/equipos/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Equipo eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar equipo");
    }
  };

  const handleEditTeam = (equipo) => {
    setEditingTeam({ ...equipo });
    setShowEditTeamModal(true);
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/equipos/${editingTeam.id}`, {
        nombre: editingTeam.nombre,
        color: editingTeam.color
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Equipo actualizado");
      setShowEditTeamModal(false);
      setEditingTeam(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleEditPartido = (partido) => {
    setEditingPartido({ ...partido });
    setShowEditPartidoModal(true);
  };

  const handleUpdatePartido = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/partidos/${editingPartido.id}`, {
        fecha: editingPartido.fecha,
        hora: editingPartido.hora,
        cancha: editingPartido.cancha,
        fase: editingPartido.fase
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Partido actualizado");
      setShowEditPartidoModal(false);
      setEditingPartido(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleAssignPlayer = async (participanteId) => {
    try {
      await axios.post(
        `${API}/admin/equipos/${selectedTeam.id}/jugadores/${participanteId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleAssignMultiplePlayers = async () => {
    if (selectedPlayers.length === 0) return;
    
    let successCount = 0;
    for (const playerId of selectedPlayers) {
      const success = await handleAssignPlayer(playerId);
      if (success) successCount++;
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} jugador${successCount > 1 ? 'es' : ''} asignado${successCount > 1 ? 's' : ''}`);
      fetchData();
    }
    setSelectedPlayers([]);
    setShowAssignPlayerModal(false);
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleRemovePlayer = async (equipoId, participanteId) => {
    try {
      await axios.delete(
        `${API}/admin/equipos/${equipoId}/jugadores/${participanteId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Jugador removido");
      fetchData();
    } catch (error) {
      toast.error("Error al remover jugador");
    }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/partidos`, newMatch, {
        headers: getAuthHeaders()
      });
      toast.success("Partido creado");
      setShowNewMatchModal(false);
      setNewMatch({
        equipo_local_id: "",
        equipo_visitante_id: "",
        fecha: "",
        hora: "",
        cancha: "",
        fase: "Fecha 1"
      });
      fetchData();
    } catch (error) {
      toast.error("Error al crear partido");
    }
  };

  const handleUpdateResult = async (partidoId, golesLocal, golesVisitante) => {
    try {
      await axios.put(
        `${API}/admin/partidos/${partidoId}/resultado`,
        { goles_local: parseInt(golesLocal), goles_visitante: parseInt(golesVisitante) },
        { headers: getAuthHeaders() }
      );
      toast.success("Resultado actualizado");
      setEditingResult(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar resultado");
    }
  };

  const handleDeleteMatch = async (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este partido?")) return;
    try {
      await axios.delete(`${API}/admin/partidos/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Partido eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar partido");
    }
  };

  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/info-torneo`, infoTorneo, {
        headers: getAuthHeaders()
      });
      toast.success("Información actualizada");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  // Galeria handlers
  const handleCreateGaleriaItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/galeria`, newGaleriaItem, {
        headers: getAuthHeaders()
      });
      toast.success("Contenido agregado a la galería");
      setShowNewGaleriaModal(false);
      setNewGaleriaItem({
        tipo: "imagen",
        url: "",
        titulo: "",
        descripcion: "",
        thumbnail: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Error al agregar contenido");
    }
  };

  const handleEditGaleriaItem = (item) => {
    setEditingGaleriaItem({ ...item });
    setShowEditGaleriaModal(true);
  };

  const handleUpdateGaleriaItem = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/galeria/${editingGaleriaItem.id}`, {
        tipo: editingGaleriaItem.tipo,
        url: editingGaleriaItem.url,
        titulo: editingGaleriaItem.titulo,
        descripcion: editingGaleriaItem.descripcion,
        thumbnail: editingGaleriaItem.thumbnail
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Contenido actualizado");
      setShowEditGaleriaModal(false);
      setEditingGaleriaItem(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDeleteGaleriaItem = async (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este contenido de la galería?")) return;
    try {
      await axios.delete(`${API}/admin/galeria/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Contenido eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al subir archivo";
      toast.error(errorMsg);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCreateGaleriaWithFile = async (e) => {
    e.preventDefault();
    
    const fileInput = document.getElementById('galeria-file-input');
    const file = fileInput?.files?.[0];
    
    if (uploadMode === "file" && file) {
      // Upload file first
      const uploadResult = await handleFileUpload(file);
      if (!uploadResult) return;
      
      // Create galeria item with uploaded file URL
      try {
        await axios.post(`${API}/admin/galeria`, {
          tipo: uploadResult.tipo,
          url: uploadResult.url,
          titulo: newGaleriaItem.titulo,
          descripcion: newGaleriaItem.descripcion,
          thumbnail: newGaleriaItem.thumbnail
        }, {
          headers: getAuthHeaders()
        });
        toast.success("Contenido agregado a la galería");
        setShowNewGaleriaModal(false);
        setNewGaleriaItem({
          tipo: "imagen",
          url: "",
          titulo: "",
          descripcion: "",
          thumbnail: ""
        });
        setUploadMode("file");
        fetchData();
      } catch (error) {
        toast.error("Error al agregar contenido");
      }
    } else {
      // Use URL mode
      handleCreateGaleriaItem(e);
    }
  };

  const unassignedPlayers = participantes.filter(p => !p.equipo_id);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-lactal-base flex items-center justify-center p-4" data-testid="admin-login-page">
        <Toaster position="top-center" />
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="LACTAL" className="h-16 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white uppercase">
              Panel de Admin
            </h1>
          </div>
          
          <form onSubmit={handleLogin} className="bg-lactal-dark p-6 rounded-lg border border-white/5">
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Usuario</Label>
                <Input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="input-dark mt-1"
                  placeholder="admin"
                  data-testid="admin-username"
                />
              </div>
              <div>
                <Label className="text-gray-300">Contraseña</Label>
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="input-dark mt-1"
                  placeholder="••••••••"
                  data-testid="admin-password"
                />
              </div>
              <Button
                type="submit"
                disabled={authLoading}
                className="w-full btn-primary"
                data-testid="admin-login-submit"
              >
                <span>{authLoading ? "Entrando..." : "Entrar"}</span>
              </Button>
            </div>
          </form>
          
          <button
            onClick={() => navigate("/")}
            className="mt-4 text-gray-400 hover:text-white flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} />
            Volver al sitio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lactal-base" data-testid="admin-panel">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-lactal-dark border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="LACTAL" className="h-8" />
            <span className="font-heading text-lg text-white uppercase hidden sm:block">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="text-gray-400 hover:text-white p-2"
              title="Ver sitio"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-white p-2"
              title="Cerrar sesión"
              data-testid="admin-logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto p-4">
        <Tabs defaultValue="participantes" className="w-full">
          <TabsList className="w-full justify-start bg-lactal-dark border-b border-white/5 rounded-none p-0 h-auto mb-6 overflow-x-auto">
            <TabsTrigger 
              value="participantes" 
              className="data-[state=active]:bg-transparent data-[state=active]:text-lactal-gold data-[state=active]:border-b-2 data-[state=active]:border-lactal-gold rounded-none px-4 py-3"
            >
              <Users size={18} className="mr-2" />
              Inscriptos ({participantes.length})
            </TabsTrigger>
            <TabsTrigger 
              value="equipos"
              className="data-[state=active]:bg-transparent data-[state=active]:text-lactal-gold data-[state=active]:border-b-2 data-[state=active]:border-lactal-gold rounded-none px-4 py-3"
            >
              <Shield size={18} className="mr-2" />
              Equipos ({equipos.length})
            </TabsTrigger>
            <TabsTrigger 
              value="partidos"
              className="data-[state=active]:bg-transparent data-[state=active]:text-lactal-gold data-[state=active]:border-b-2 data-[state=active]:border-lactal-gold rounded-none px-4 py-3"
            >
              <Calendar size={18} className="mr-2" />
              Fixture ({partidos.length})
            </TabsTrigger>
            <TabsTrigger 
              value="info"
              className="data-[state=active]:bg-transparent data-[state=active]:text-lactal-gold data-[state=active]:border-b-2 data-[state=active]:border-lactal-gold rounded-none px-4 py-3"
            >
              <Info size={18} className="mr-2" />
              Info
            </TabsTrigger>
            <TabsTrigger 
              value="galeria"
              className="data-[state=active]:bg-transparent data-[state=active]:text-lactal-gold data-[state=active]:border-b-2 data-[state=active]:border-lactal-gold rounded-none px-4 py-3"
            >
              <Image size={18} className="mr-2" />
              Galería ({galeria.length})
            </TabsTrigger>
          </TabsList>

          {/* Participantes Tab */}
          <TabsContent value="participantes" className="mt-0">
            <div className="flex justify-end mb-4">
              <a
                href={`${API}/admin/export/participantes`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  const auth = btoa(`${credentials.username}:${credentials.password}`);
                  window.open(`${API}/admin/export/participantes`, '_blank');
                }}
              >
                <Download size={16} />
                Exportar Excel
              </a>
            </div>
            <div className="admin-card overflow-x-auto">
              {loading ? (
                <p className="text-gray-400 text-center py-8">Cargando...</p>
              ) : participantes.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No hay inscriptos aún</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Jugador</th>
                      <th>Apodo</th>
                      <th>Edad</th>
                      <th>Teléfono</th>
                      <th>Posición</th>
                      <th>Nivel</th>
                      <th>Comida</th>
                      <th>Equipo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantes.map((p) => {
                      const equipo = equipos.find(e => e.id === p.equipo_id);
                      return (
                        <tr key={p.id}>
                          <td className="text-white font-medium">
                            {p.nombre} {p.apellido}
                          </td>
                          <td className="text-gray-400">{p.apodo || "-"}</td>
                          <td className="text-gray-400">{p.edad}</td>
                          <td className="text-gray-400">{p.telefono || "-"}</td>
                          <td><PositionBadge position={p.posicion} /></td>
                          <td>
                            <span className="text-lactal-gold font-bold">{p.nivel}</span>
                          </td>
                          <td>
                            {p.comida_final ? (
                              <span className="text-green-400 text-sm" title="Se queda a la comida">🍕 Sí</span>
                            ) : (
                              <span className="text-gray-500 text-sm" title="No se queda">❌ No</span>
                            )}
                          </td>
                          <td>
                            {equipo ? (
                              <span 
                                className="px-2 py-1 rounded text-xs font-medium"
                                style={{ backgroundColor: equipo.color + '30', color: equipo.color }}
                              >
                                {equipo.nombre}
                              </span>
                            ) : (
                              <span className="text-gray-500">Sin asignar</span>
                            )}
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEditPlayer(p)}
                                className="text-lactal-gold hover:text-yellow-300 p-1"
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteParticipante(p.id)}
                                className="text-red-400 hover:text-red-300 p-1"
                                title="Eliminar"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* Equipos Tab */}
          <TabsContent value="equipos" className="mt-0">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl text-white uppercase">
                Equipos
              </h2>
              <div className="flex gap-2">
                {equipos.length > 0 && (
                  <a
                    href={`${API}/admin/export/equipos`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Download size={16} />
                    Exportar Excel
                  </a>
                )}
                <Button
                  onClick={() => setShowNewTeamModal(true)}
                  className="btn-primary text-sm"
                  data-testid="create-team-btn"
                >
                  <span className="flex items-center gap-2">
                    <Plus size={16} />
                    Nuevo Equipo
                  </span>
                </Button>
              </div>
            </div>

            {equipos.length === 0 ? (
              <div className="admin-card text-center py-8">
                <p className="text-gray-400">No hay equipos creados</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {equipos.map((equipo) => (
                  <div 
                    key={equipo.id}
                    className="admin-card"
                    style={{ borderLeftColor: equipo.color, borderLeftWidth: '4px' }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg font-bold text-white uppercase">
                        {equipo.nombre}
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditTeam(equipo)}
                          className="text-blue-400 hover:text-blue-300 p-1"
                          title="Editar equipo"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedTeam(equipo);
                            setShowAssignPlayerModal(true);
                          }}
                          className="text-lactal-gold hover:text-yellow-400 p-1"
                          title="Agregar jugador"
                        >
                          <UserPlus size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(equipo.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Eliminar equipo"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    
                    {equipo.jugadores && equipo.jugadores.length > 0 ? (
                      <div className="space-y-2">
                        {equipo.jugadores.map(j => (
                          <div key={j.id} className="flex items-center justify-between bg-black/20 p-2 rounded">
                            <div className="flex items-center gap-2">
                              <PositionBadge position={j.posicion} />
                              <span className="text-gray-300 text-sm">
                                {j.apodo || `${j.nombre} ${j.apellido}`}
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemovePlayer(equipo.id, j.id)}
                              className="text-gray-500 hover:text-red-400"
                              title="Quitar del equipo"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Sin jugadores</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Partidos Tab */}
          <TabsContent value="partidos" className="mt-0">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl text-white uppercase">
                Fixture
              </h2>
              <Button
                onClick={() => setShowNewMatchModal(true)}
                className="btn-primary text-sm"
                disabled={equipos.length < 2}
                data-testid="create-match-btn"
              >
                <span className="flex items-center gap-2">
                  <Plus size={16} />
                  Nuevo Partido
                </span>
              </Button>
            </div>

            {equipos.length < 2 && (
              <p className="text-yellow-500 text-sm mb-4">
                Necesitás al menos 2 equipos para crear partidos.
              </p>
            )}

            {partidos.length === 0 ? (
              <div className="admin-card text-center py-8">
                <p className="text-gray-400">No hay partidos programados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {partidos.map((partido) => (
                  <div 
                    key={partido.id}
                    className="admin-card"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 justify-center sm:justify-start">
                          <span 
                            className="font-heading font-bold text-lg"
                            style={{ color: partido.equipo_local?.color }}
                          >
                            {partido.equipo_local?.nombre || "TBD"}
                          </span>
                          
                          {editingResult === partido.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                defaultValue={partido.goles_local || 0}
                                className="w-12 h-8 text-center bg-black/30 border border-white/10 rounded text-white"
                                id={`local-${partido.id}`}
                              />
                              <span className="text-gray-500">-</span>
                              <input
                                type="number"
                                min="0"
                                defaultValue={partido.goles_visitante || 0}
                                className="w-12 h-8 text-center bg-black/30 border border-white/10 rounded text-white"
                                id={`visitante-${partido.id}`}
                              />
                              <button
                                onClick={() => {
                                  const local = document.getElementById(`local-${partido.id}`).value;
                                  const visitante = document.getElementById(`visitante-${partido.id}`).value;
                                  handleUpdateResult(partido.id, local, visitante);
                                }}
                                className="text-green-400 hover:text-green-300 p-1"
                              >
                                <Check size={18} />
                              </button>
                              <button
                                onClick={() => setEditingResult(null)}
                                className="text-gray-400 hover:text-white p-1"
                              >
                                <X size={18} />
                              </button>
                            </div>
                          ) : (
                            <span className="px-3 py-1 bg-black/30 rounded font-heading text-lg">
                              {partido.jugado 
                                ? `${partido.goles_local} - ${partido.goles_visitante}`
                                : "VS"
                              }
                            </span>
                          )}
                          
                          <span 
                            className="font-heading font-bold text-lg"
                            style={{ color: partido.equipo_visitante?.color }}
                          >
                            {partido.equipo_visitante?.nombre || "TBD"}
                          </span>
                        </div>
                        
                        {(partido.fecha || partido.hora || partido.cancha || partido.fase) && (
                          <p className="text-gray-500 text-sm text-center sm:text-left mt-2">
                            {partido.fase && <span className="text-lactal-gold">{partido.fase}</span>}
                            {partido.fase && (partido.fecha || partido.hora || partido.cancha) && " • "}
                            {[partido.fecha, partido.hora, partido.cancha].filter(Boolean).join(" • ")}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 justify-center">
                        <button
                          onClick={() => handleEditPartido(partido)}
                          className="text-blue-400 hover:text-blue-300 p-1"
                          title="Editar partido"
                        >
                          <Calendar size={18} />
                        </button>
                        <button
                          onClick={() => setEditingResult(partido.id)}
                          className="text-lactal-gold hover:text-yellow-400 p-1"
                          title="Editar resultado"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteMatch(partido.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Eliminar partido"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Info Tab */}
          <TabsContent value="info" className="mt-0">
            {infoTorneo && (
              <form onSubmit={handleUpdateInfo} className="admin-card space-y-4">
                <div>
                  <Label className="text-gray-300">Lugar</Label>
                  <Input
                    value={infoTorneo.lugar}
                    onChange={(e) => setInfoTorneo(prev => ({ ...prev, lugar: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Fechas</Label>
                  <Input
                    value={infoTorneo.fechas}
                    onChange={(e) => setInfoTorneo(prev => ({ ...prev, fechas: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Duración de partidos</Label>
                  <Input
                    value={infoTorneo.duracion_partidos}
                    onChange={(e) => setInfoTorneo(prev => ({ ...prev, duracion_partidos: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Formato</Label>
                  <Input
                    value={infoTorneo.formato}
                    onChange={(e) => setInfoTorneo(prev => ({ ...prev, formato: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Reglamento</Label>
                  <textarea
                    value={infoTorneo.reglamento}
                    onChange={(e) => setInfoTorneo(prev => ({ ...prev, reglamento: e.target.value }))}
                    className="input-dark mt-1 min-h-[100px] py-3"
                    rows={4}
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Mensaje extra</Label>
                  <Input
                    value={infoTorneo.mensaje_extra}
                    onChange={(e) => setInfoTorneo(prev => ({ ...prev, mensaje_extra: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
                <Button type="submit" className="btn-primary">
                  <span className="flex items-center gap-2">
                    <Save size={16} />
                    Guardar cambios
                  </span>
                </Button>
              </form>
            )}
          </TabsContent>

          {/* Galeria Tab */}
          <TabsContent value="galeria" className="mt-0">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl text-white uppercase">
                Galería de Imágenes y Videos
              </h2>
              <Button
                onClick={() => setShowNewGaleriaModal(true)}
                className="btn-primary text-sm"
                data-testid="add-galeria-btn"
              >
                <span className="flex items-center gap-2">
                  <Plus size={16} />
                  Agregar Contenido
                </span>
              </Button>
            </div>

            {galeria.length === 0 ? (
              <div className="admin-card text-center py-8">
                <Image size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">No hay contenido en la galería</p>
                <p className="text-gray-500 text-sm mt-2">
                  Agregá imágenes o videos para mostrar en la página principal
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {galeria.map((item) => (
                  <div 
                    key={item.id}
                    className="admin-card p-0 overflow-hidden"
                  >
                    <div className="relative aspect-video bg-black/30">
                      {item.tipo === 'video' ? (
                        <>
                          <img 
                            src={item.thumbnail || 'https://placehold.co/400x225/1a1a1a/666?text=Video'} 
                            alt={item.titulo || 'Video'}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            <Play size={40} className="text-white opacity-80" />
                          </div>
                        </>
                      ) : (
                        <img 
                          src={item.url} 
                          alt={item.titulo || 'Imagen'}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          item.tipo === 'video' 
                            ? 'bg-red-600/80 text-white' 
                            : 'bg-blue-600/80 text-white'
                        }`}>
                          {item.tipo === 'video' ? 'VIDEO' : 'IMAGEN'}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-white font-bold truncate">
                        {item.titulo || 'Sin título'}
                      </h3>
                      {item.descripcion && (
                        <p className="text-gray-400 text-sm truncate mt-1">
                          {item.descripcion}
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-2 truncate">
                        {item.url}
                      </p>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                        <button
                          onClick={() => handleEditGaleriaItem(item)}
                          className="flex-1 flex items-center justify-center gap-1 text-lactal-gold hover:text-yellow-300 py-1"
                          title="Editar"
                        >
                          <Edit size={16} />
                          <span className="text-sm">Editar</span>
                        </button>
                        <button
                          onClick={() => handleDeleteGaleriaItem(item.id)}
                          className="flex-1 flex items-center justify-center gap-1 text-red-400 hover:text-red-300 py-1"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                          <span className="text-sm">Eliminar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* New Team Modal */}
      <Dialog open={showNewTeamModal} onOpenChange={setShowNewTeamModal}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Nuevo Equipo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTeam} className="space-y-4">
            <div>
              <Label className="text-gray-300">Nombre del equipo</Label>
              <Input
                value={newTeam.nombre}
                onChange={(e) => setNewTeam(prev => ({ ...prev, nombre: e.target.value }))}
                className="input-dark mt-1"
                placeholder="Los Cracks"
                required
              />
            </div>
            <div>
              <Label className="text-gray-300">Color</Label>
              <Select
                value={newTeam.color}
                onValueChange={(value) => setNewTeam(prev => ({ ...prev, color: value }))}
              >
                <SelectTrigger className="input-dark mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {TEAM_COLORS.map((color) => (
                    <SelectItem 
                      key={color.value} 
                      value={color.value}
                      className="text-white hover:bg-white/10"
                    >
                      <span className="flex items-center gap-2">
                        <span 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: color.value }}
                        />
                        {color.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewTeamModal(false)}
                className="flex-1 btn-secondary"
              >
                <span>Cancelar</span>
              </Button>
              <Button type="submit" className="flex-1 btn-primary">
                <span>Crear</span>
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Player Modal - Multi Selection */}
      <Dialog open={showAssignPlayerModal} onOpenChange={(open) => {
        setShowAssignPlayerModal(open);
        if (!open) setSelectedPlayers([]);
      }}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Asignar jugadores a {selectedTeam?.nombre}
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-sm mb-2">
            Seleccioná los jugadores que querés agregar al equipo
          </p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {unassignedPlayers.length === 0 ? (
              <p className="text-gray-400 text-center py-4">
                No hay jugadores sin equipo
              </p>
            ) : (
              unassignedPlayers.map((p) => (
                <button
                  key={p.id}
                  onClick={() => togglePlayerSelection(p.id)}
                  className={`w-full flex items-center justify-between p-3 rounded transition-colors ${
                    selectedPlayers.includes(p.id)
                      ? 'bg-lactal-gold/20 border border-lactal-gold/50'
                      : 'bg-black/20 hover:bg-black/40 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedPlayers.includes(p.id)
                        ? 'bg-lactal-gold border-lactal-gold'
                        : 'border-gray-500'
                    }`}>
                      {selectedPlayers.includes(p.id) && <Check size={14} className="text-black" />}
                    </div>
                    <PositionBadge position={p.posicion} />
                    <span className="text-white">
                      {p.apodo || `${p.nombre} ${p.apellido}`}
                    </span>
                  </div>
                  <span className="text-lactal-gold font-bold">{p.nivel}</span>
                </button>
              ))
            )}
          </div>
          {unassignedPlayers.length > 0 && (
            <div className="flex gap-3 pt-4 border-t border-white/10 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAssignPlayerModal(false);
                  setSelectedPlayers([]);
                }}
                className="flex-1 btn-secondary"
              >
                <span>Cancelar</span>
              </Button>
              <Button 
                onClick={handleAssignMultiplePlayers}
                disabled={selectedPlayers.length === 0}
                className="flex-1 btn-primary"
              >
                <span>
                  {selectedPlayers.length === 0 
                    ? 'Seleccioná jugadores' 
                    : `Agregar ${selectedPlayers.length} jugador${selectedPlayers.length > 1 ? 'es' : ''}`
                  }
                </span>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Match Modal */}
      <Dialog open={showNewMatchModal} onOpenChange={setShowNewMatchModal}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Nuevo Partido
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMatch} className="space-y-4">
            <div>
              <Label className="text-gray-300">Equipo Local</Label>
              <Select
                value={newMatch.equipo_local_id}
                onValueChange={(value) => setNewMatch(prev => ({ ...prev, equipo_local_id: value }))}
              >
                <SelectTrigger className="input-dark mt-1">
                  <SelectValue placeholder="Seleccionar equipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {equipos.map((e) => (
                    <SelectItem 
                      key={e.id} 
                      value={e.id}
                      className="text-white hover:bg-white/10"
                      disabled={e.id === newMatch.equipo_visitante_id}
                    >
                      {e.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Equipo Visitante</Label>
              <Select
                value={newMatch.equipo_visitante_id}
                onValueChange={(value) => setNewMatch(prev => ({ ...prev, equipo_visitante_id: value }))}
              >
                <SelectTrigger className="input-dark mt-1">
                  <SelectValue placeholder="Seleccionar equipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {equipos.map((e) => (
                    <SelectItem 
                      key={e.id} 
                      value={e.id}
                      className="text-white hover:bg-white/10"
                      disabled={e.id === newMatch.equipo_local_id}
                    >
                      {e.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Fecha</Label>
                <Input
                  type="text"
                  value={newMatch.fecha}
                  onChange={(e) => setNewMatch(prev => ({ ...prev, fecha: e.target.value }))}
                  className="input-dark mt-1"
                  placeholder="15/01/2026"
                />
              </div>
              <div>
                <Label className="text-gray-300">Hora</Label>
                <Input
                  type="text"
                  value={newMatch.hora}
                  onChange={(e) => setNewMatch(prev => ({ ...prev, hora: e.target.value }))}
                  className="input-dark mt-1"
                  placeholder="20:00"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Cancha</Label>
              <Input
                value={newMatch.cancha}
                onChange={(e) => setNewMatch(prev => ({ ...prev, cancha: e.target.value }))}
                className="input-dark mt-1"
                placeholder="Cancha 1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Fase del torneo</Label>
              <Select
                value={newMatch.fase}
                onValueChange={(value) => setNewMatch(prev => ({ ...prev, fase: value }))}
              >
                <SelectTrigger className="input-dark mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {FASES_TORNEO.map((fase) => (
                    <SelectItem 
                      key={fase} 
                      value={fase}
                      className="text-white hover:bg-white/10"
                    >
                      {fase}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewMatchModal(false)}
                className="flex-1 btn-secondary"
              >
                <span>Cancelar</span>
              </Button>
              <Button 
                type="submit" 
                className="flex-1 btn-primary"
                disabled={!newMatch.equipo_local_id || !newMatch.equipo_visitante_id}
              >
                <span>Crear</span>
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Player Modal */}
      <Dialog open={showEditPlayerModal} onOpenChange={(open) => {
        setShowEditPlayerModal(open);
        if (!open) setEditingPlayer(null);
      }}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Editar Jugador
            </DialogTitle>
          </DialogHeader>
          {editingPlayer && (
            <form onSubmit={handleUpdatePlayer} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Nombre</Label>
                  <Input
                    value={editingPlayer.nombre}
                    onChange={(e) => setEditingPlayer(prev => ({ ...prev, nombre: e.target.value }))}
                    className="input-dark mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Apellido</Label>
                  <Input
                    value={editingPlayer.apellido}
                    onChange={(e) => setEditingPlayer(prev => ({ ...prev, apellido: e.target.value }))}
                    className="input-dark mt-1"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Apodo</Label>
                <Input
                  value={editingPlayer.apodo || ""}
                  onChange={(e) => setEditingPlayer(prev => ({ ...prev, apodo: e.target.value }))}
                  className="input-dark mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Edad</Label>
                  <Input
                    type="number"
                    value={editingPlayer.edad}
                    onChange={(e) => setEditingPlayer(prev => ({ ...prev, edad: e.target.value }))}
                    className="input-dark mt-1"
                    min="18"
                    max="99"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Teléfono</Label>
                  <Input
                    value={editingPlayer.telefono || ""}
                    onChange={(e) => setEditingPlayer(prev => ({ ...prev, telefono: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Posición</Label>
                <Select
                  value={editingPlayer.posicion}
                  onValueChange={(value) => setEditingPlayer(prev => ({ ...prev, posicion: value }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="Golero" className="text-white hover:bg-white/10">Golero</SelectItem>
                    <SelectItem value="Defensa" className="text-white hover:bg-white/10">Defensa</SelectItem>
                    <SelectItem value="Volante" className="text-white hover:bg-white/10">Volante</SelectItem>
                    <SelectItem value="Delantero" className="text-white hover:bg-white/10">Delantero</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Nivel ({editingPlayer.nivel})</Label>
                <Input
                  type="range"
                  value={editingPlayer.nivel}
                  onChange={(e) => setEditingPlayer(prev => ({ ...prev, nivel: e.target.value }))}
                  className="w-full mt-1"
                  min="1"
                  max="100"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-comida"
                  checked={editingPlayer.comida_final}
                  onChange={(e) => setEditingPlayer(prev => ({ ...prev, comida_final: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-comida" className="text-gray-300 cursor-pointer">
                  🍕 Se queda a la comida
                </Label>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditPlayerModal(false);
                    setEditingPlayer(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  <span>Cancelar</span>
                </Button>
                <Button type="submit" className="flex-1 btn-primary">
                  <span>Guardar</span>
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* New Galeria Item Modal */}
      <Dialog open={showNewGaleriaModal} onOpenChange={(open) => {
        setShowNewGaleriaModal(open);
        if (!open) {
          setUploadMode("file");
          setNewGaleriaItem({
            tipo: "imagen",
            url: "",
            titulo: "",
            descripcion: "",
            thumbnail: ""
          });
        }
      }}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Agregar a Galería
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateGaleriaWithFile} className="space-y-4">
            {/* Toggle between file upload and URL */}
            <div className="flex gap-2 p-1 bg-black/30 rounded-lg">
              <button
                type="button"
                onClick={() => setUploadMode("file")}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  uploadMode === "file" 
                    ? "bg-lactal-gold text-black" 
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Upload size={16} />
                Subir archivo
              </button>
              <button
                type="button"
                onClick={() => setUploadMode("url")}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  uploadMode === "url" 
                    ? "bg-lactal-gold text-black" 
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Link size={16} />
                Usar URL
              </button>
            </div>

            {uploadMode === "file" ? (
              <>
                {/* File upload mode */}
                <div>
                  <Label className="text-gray-300">Seleccionar archivo</Label>
                  <div className="mt-1">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-lactal-gold/50 transition-colors bg-black/20">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-2 text-gray-400" />
                        <p className="mb-1 text-sm text-gray-400">
                          <span className="font-semibold text-lactal-gold">Click para seleccionar</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Imágenes (JPG, PNG, GIF) o Videos (MP4, MOV)
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Máximo 100MB
                        </p>
                      </div>
                      <input
                        id="galeria-file-input"
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Auto-detect type
                            const isVideo = file.type.startsWith('video/');
                            setNewGaleriaItem(prev => ({ 
                              ...prev, 
                              tipo: isVideo ? 'video' : 'imagen' 
                            }));
                          }
                        }}
                      />
                    </label>
                    <p id="file-selected" className="text-sm text-gray-400 mt-2 text-center">
                      {/* JavaScript will update this */}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* URL mode */}
                <div>
                  <Label className="text-gray-300">Tipo de contenido</Label>
                  <Select
                    value={newGaleriaItem.tipo}
                    onValueChange={(value) => setNewGaleriaItem(prev => ({ ...prev, tipo: value }))}
                  >
                    <SelectTrigger className="input-dark mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="imagen" className="text-white hover:bg-white/10">
                        <span className="flex items-center gap-2">
                          <Image size={16} /> Imagen
                        </span>
                      </SelectItem>
                      <SelectItem value="video" className="text-white hover:bg-white/10">
                        <span className="flex items-center gap-2">
                          <Video size={16} /> Video (YouTube)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">
                    {newGaleriaItem.tipo === 'video' ? 'URL del video (YouTube)' : 'URL de la imagen'}
                  </Label>
                  <Input
                    value={newGaleriaItem.url}
                    onChange={(e) => setNewGaleriaItem(prev => ({ ...prev, url: e.target.value }))}
                    className="input-dark mt-1"
                    placeholder={newGaleriaItem.tipo === 'video' ? 'https://youtube.com/watch?v=...' : 'https://...'}
                    required={uploadMode === "url"}
                  />
                </div>
                {newGaleriaItem.tipo === 'video' && (
                  <div>
                    <Label className="text-gray-300">URL del thumbnail (opcional)</Label>
                    <Input
                      value={newGaleriaItem.thumbnail}
                      onChange={(e) => setNewGaleriaItem(prev => ({ ...prev, thumbnail: e.target.value }))}
                      className="input-dark mt-1"
                      placeholder="https://..."
                    />
                  </div>
                )}
              </>
            )}
            <div>
              <Label className="text-gray-300">Título (opcional)</Label>
              <Input
                value={newGaleriaItem.titulo}
                onChange={(e) => setNewGaleriaItem(prev => ({ ...prev, titulo: e.target.value }))}
                className="input-dark mt-1"
                placeholder="Gol del partido..."
              />
            </div>
            <div>
              <Label className="text-gray-300">Descripción (opcional)</Label>
              <Input
                value={newGaleriaItem.descripcion}
                onChange={(e) => setNewGaleriaItem(prev => ({ ...prev, descripcion: e.target.value }))}
                className="input-dark mt-1"
                placeholder="Descripción breve..."
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewGaleriaModal(false)}
                className="flex-1 btn-secondary"
                disabled={uploadingFile}
              >
                <span>Cancelar</span>
              </Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={uploadingFile}>
                <span>{uploadingFile ? "Subiendo..." : "Agregar"}</span>
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Galeria Item Modal */}
      <Dialog open={showEditGaleriaModal} onOpenChange={(open) => {
        setShowEditGaleriaModal(open);
        if (!open) setEditingGaleriaItem(null);
      }}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Editar Contenido
            </DialogTitle>
          </DialogHeader>
          {editingGaleriaItem && (
            <form onSubmit={handleUpdateGaleriaItem} className="space-y-4">
              <div>
                <Label className="text-gray-300">Tipo de contenido</Label>
                <Select
                  value={editingGaleriaItem.tipo}
                  onValueChange={(value) => setEditingGaleriaItem(prev => ({ ...prev, tipo: value }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="imagen" className="text-white hover:bg-white/10">
                      <span className="flex items-center gap-2">
                        <Image size={16} /> Imagen
                      </span>
                    </SelectItem>
                    <SelectItem value="video" className="text-white hover:bg-white/10">
                      <span className="flex items-center gap-2">
                        <Video size={16} /> Video
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">
                  {editingGaleriaItem.tipo === 'video' ? 'URL del video (YouTube)' : 'URL de la imagen'}
                </Label>
                <Input
                  value={editingGaleriaItem.url}
                  onChange={(e) => setEditingGaleriaItem(prev => ({ ...prev, url: e.target.value }))}
                  className="input-dark mt-1"
                  required
                />
              </div>
              {editingGaleriaItem.tipo === 'video' && (
                <div>
                  <Label className="text-gray-300">URL del thumbnail (opcional)</Label>
                  <Input
                    value={editingGaleriaItem.thumbnail || ""}
                    onChange={(e) => setEditingGaleriaItem(prev => ({ ...prev, thumbnail: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
              )}
              <div>
                <Label className="text-gray-300">Título (opcional)</Label>
                <Input
                  value={editingGaleriaItem.titulo || ""}
                  onChange={(e) => setEditingGaleriaItem(prev => ({ ...prev, titulo: e.target.value }))}
                  className="input-dark mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Descripción (opcional)</Label>
                <Input
                  value={editingGaleriaItem.descripcion || ""}
                  onChange={(e) => setEditingGaleriaItem(prev => ({ ...prev, descripcion: e.target.value }))}
                  className="input-dark mt-1"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditGaleriaModal(false);
                    setEditingGaleriaItem(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  <span>Cancelar</span>
                </Button>
                <Button type="submit" className="flex-1 btn-primary">
                  <span>Guardar</span>
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Team Modal */}
      <Dialog open={showEditTeamModal} onOpenChange={(open) => {
        setShowEditTeamModal(open);
        if (!open) setEditingTeam(null);
      }}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Editar Equipo
            </DialogTitle>
          </DialogHeader>
          {editingTeam && (
            <form onSubmit={handleUpdateTeam} className="space-y-4">
              <div>
                <Label className="text-gray-300">Nombre del equipo</Label>
                <Input
                  value={editingTeam.nombre}
                  onChange={(e) => setEditingTeam(prev => ({ ...prev, nombre: e.target.value }))}
                  className="input-dark mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Color (chaleco)</Label>
                <Select
                  value={editingTeam.color}
                  onValueChange={(value) => setEditingTeam(prev => ({ ...prev, color: value }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {TEAM_COLORS.map((color) => (
                      <SelectItem 
                        key={color.value} 
                        value={color.value}
                        className="text-white hover:bg-white/10"
                      >
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-4 h-4 rounded border border-white/30"
                            style={{ backgroundColor: color.value }}
                          />
                          {color.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div 
                  className="mt-2 h-8 rounded border border-white/20 flex items-center justify-center text-sm font-medium"
                  style={{ backgroundColor: editingTeam.color, color: editingTeam.color === '#000000' || editingTeam.color === '#1E40AF' ? '#fff' : '#000' }}
                >
                  Vista previa
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditTeamModal(false);
                    setEditingTeam(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  <span>Cancelar</span>
                </Button>
                <Button type="submit" className="flex-1 btn-primary">
                  <span>Guardar</span>
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Partido Modal */}
      <Dialog open={showEditPartidoModal} onOpenChange={(open) => {
        setShowEditPartidoModal(open);
        if (!open) setEditingPartido(null);
      }}>
        <DialogContent className="bg-[#3a3a3a] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Editar Partido
            </DialogTitle>
          </DialogHeader>
          {editingPartido && (
            <form onSubmit={handleUpdatePartido} className="space-y-4">
              <div className="bg-black/30 p-3 rounded-lg text-center">
                <span style={{ color: editingPartido.equipo_local?.color }} className="font-bold">
                  {editingPartido.equipo_local?.nombre}
                </span>
                <span className="text-gray-500 mx-2">vs</span>
                <span style={{ color: editingPartido.equipo_visitante?.color }} className="font-bold">
                  {editingPartido.equipo_visitante?.nombre}
                </span>
              </div>
              <div>
                <Label className="text-gray-300">Fase del torneo</Label>
                <Select
                  value={editingPartido.fase || "Fecha 1"}
                  onValueChange={(value) => setEditingPartido(prev => ({ ...prev, fase: value }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {FASES_TORNEO.map((fase) => (
                      <SelectItem 
                        key={fase} 
                        value={fase}
                        className="text-white hover:bg-white/10"
                      >
                        {fase}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Fecha</Label>
                  <Input
                    type="text"
                    value={editingPartido.fecha || ""}
                    onChange={(e) => setEditingPartido(prev => ({ ...prev, fecha: e.target.value }))}
                    className="input-dark mt-1"
                    placeholder="04/02/2026"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Hora</Label>
                  <Input
                    type="text"
                    value={editingPartido.hora || ""}
                    onChange={(e) => setEditingPartido(prev => ({ ...prev, hora: e.target.value }))}
                    className="input-dark mt-1"
                    placeholder="20:00"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Cancha</Label>
                <Input
                  value={editingPartido.cancha || ""}
                  onChange={(e) => setEditingPartido(prev => ({ ...prev, cancha: e.target.value }))}
                  className="input-dark mt-1"
                  placeholder="Cancha 1"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditPartidoModal(false);
                    setEditingPartido(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  <span>Cancelar</span>
                </Button>
                <Button type="submit" className="flex-1 btn-primary">
                  <span>Guardar</span>
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
